import React, { useEffect, useState, useCallback } from "react";
import { AiOutlineEdit, AiOutlineDelete } from "react-icons/ai";
import {
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
  Spinner,
} from "reactstrap";

import { showToast } from "../../../utils/toast";
import { formatTableDateTime12H } from "../../../utils/dateFormatter";
import axios from "axios";
// ================================
// API FUNCTIONS (Role-aware)
// ================================
const API_URL = process.env.REACT_APP_API_URL;

const createBankActAPI = (role) => {
  let baseUrl;
  
  if (role === "superadmin") {
    baseUrl = `${API_URL}/bank-acts`;
  } else if (role === "admin") {
    baseUrl = `${API_URL}/admin/bank-acts`;
  } else if (role === "subadmin") {
    baseUrl = `${API_URL}/sub-admin/bank-acts`;
  } else {
    baseUrl = `${API_URL}/bank-acts`; // fallback
  }
  
  // Get token for authentication
  const getToken = () => sessionStorage.getItem("token") || localStorage.getItem("token");
  const authHeaders = {
    headers: { 
      Authorization: `Bearer ${getToken()}`,
      "Content-Type": "application/json" 
    }
  };
  
  return {
    getBankActs: () => axios.get(baseUrl, authHeaders),
    createBankAct: (payload) => axios.post(baseUrl, payload, authHeaders),
    updateBankAct: (id, payload) => axios.put(`${baseUrl}/${id}`, payload, authHeaders),
    deleteBankAct: (id) => axios.delete(`${baseUrl}/${id}`, authHeaders),
  };
};

// ================================
// MAIN COMPONENT
// ================================
const BankAct = ({ role = "superadmin" }) => {
  // ---------- State ----------
  const [bankActs, setBankActs] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(""); // add | edit | delete
  const [selectedAct, setSelectedAct] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
  });

  // ---------- Fetch ----------
  const fetchBankActs = useCallback(async () => {
    console.log('🚀 Starting fetchBankActs with role:', role);
    setLoading(true);
    try {
      const api = createBankActAPI(role);
      const res = await api.getBankActs();
      console.log('📦 BankActs API response:', res);
      console.log("res", res.data.data);
      setBankActs(res?.data?.data || []);
      console.log(res.data.data, "oneeee");
    } catch (error) {
      console.error('❌ BankActs fetch error:', error);
      showToast("Failed to fetch Bank Acts", "danger");
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    fetchBankActs();
  }, [fetchBankActs]);

  // ---------- Search ----------
  const filteredActs = bankActs.filter(
    (act) =>
      act.title?.toLowerCase().includes(search.toLowerCase()) ||
      act.description?.toLowerCase().includes(search.toLowerCase())
  );

  // ---------- Handlers ----------
  const resetForm = () => {
    setFormData({ title: "", description: "" });
    setSelectedAct(null);
  };

  const openAddModal = () => {
    resetForm();
    setModalMode("add");
    setModalOpen(true);
  };

  const openEditModal = (act) => {
    setSelectedAct(act);
    setFormData({
      title: act.title || "",
      description: act.description || "",
    });
    setModalMode("edit");
    setModalOpen(true);
  };

  const openDeleteModal = (act) => {
    setSelectedAct(act);
    setModalMode("delete");
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ---------- Submit ----------
  const handleSubmit = async () => {
    try {
      const api = createBankActAPI(role);
      
      if (modalMode === "add") {
        const response = await api.createBankAct(formData);
        console.log("Created Response", response);
        showToast(
          response?.data?.message || "Bank Act Created successfully",
          "success"
        );
      }

      if (modalMode === "edit") {
        const response = await api.updateBankAct(selectedAct.id, formData);
        showToast(
          response?.data?.message || "Bank Act updated successfully",
          "success"
        );
      }

      if (modalMode === "delete") {
        const response = await api.deleteBankAct(selectedAct.id);
        showToast(
          response?.data?.message || "Bank Act deleted successfully",
          "success"
        );
      }

      closeModal();
      fetchBankActs();
    } catch (error) {
      //   showToast(error?.response?.data?.message || "Action failed", "danger");
      showToast(
        error?.response?.data?.error.details[0].message || "Action failed",
        "danger"
      );
    }
  };

  //   const handleToggleStatus = async (act) => {
  //     try {
  //       await toggleBankActStatus(act.id);
  //       showToast("Status updated", "success");
  //       fetchBankActs();
  //     } catch {
  //       showToast("Failed to update status", "danger");
  //     }
  //   };

  // ================================
  // RENDER
  // ================================
  return (
    <div className="p-4">
      <div className="d-flex justify-content-between mb-3">
        <h4>Bank Act Management</h4>
        <Button color="primary" onClick={openAddModal}>
          + Add Bank Act
        </Button>
      </div>

      <Input
        placeholder="Search by name ..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="mb-3"
      />

      {loading ? (
        <Spinner />
      ) : (
        <Table bordered hover responsive>
          <thead>
            <tr>
              <th>#</th>
              <th>Name</th>
              <th>Description</th>
              <th>Status</th>
              <th>Created Date</th>
              <th>Updated Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {/* {filteredActs?.length === 0 ? ( */}
            {bankActs?.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center">
                  No records found
                </td>
              </tr>
            ) : (
              //   filteredActs?.map((act, index) => (
              bankActs?.map((act, index) => (
                <tr key={act.id}>
                  <td>{index + 1}</td>
                  <td>{act.title}</td>
                  <td>{act.description || "-"}</td>
                  <td>
                    <Button
                      size="sm"
                      color={act.isActive ? "success" : "secondary"}
                      //   onClick={() => handleToggleStatus(act)}
                    >
                      {act.isActive ? "Active" : "Inactive"}
                    </Button>
                  </td>
                  <td>{formatTableDateTime12H(`${act.createdAt}`)}</td>
                  <td>{formatTableDateTime12H(`${act.updatedAt}`)}</td>

                  <td>
                    <div className="d-flex gap-1 justify-content-center">
                      {/* ← ADDED: Eye Icon for Full View */}

                      <Button
                        size="sm"
                        color="link"
                        className="om-action-btn om-btn-edit"
                        onClick={() => openEditModal(act)}
                        title="Edit"
                      >
                        <AiOutlineEdit size={16} />
                      </Button>
                      <Button
                        size="sm"
                        color="link"
                        className="om-action-btn om-btn-delete"
                        onClick={() => openDeleteModal(act)}
                        title="Delete"
                      >
                        <AiOutlineDelete size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      )}

      {/* ================= MODAL ================= */}
      <Modal isOpen={modalOpen} toggle={closeModal}>
        <ModalHeader toggle={closeModal}>
          {modalMode === "add" && "Add Bank Act"}
          {modalMode === "edit" && "Edit Bank Act"}
          {modalMode === "delete" && "Delete Bank Act"}
        </ModalHeader>

        <ModalBody>
          {modalMode === "delete" ? (
            <p>
              Are you sure you want to delete{" "}
              <strong>{selectedAct?.name}</strong>?
            </p>
          ) : (
            <>
              <FormGroup>
                <Label>Name *</Label>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                />
              </FormGroup>

              <FormGroup>
                <Label>Description</Label>
                <Input
                  type="textarea"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                />
              </FormGroup>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <Button color="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit}>
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default BankAct;

